<template>
	<section class="v2rt">
		<div class="v2rt-card">
			<div class="v2rt-card__head">
				<div class="v2rt-card__icon">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2"/></svg>
				</div>
				<div>
					<div class="v2rt-card__title">{{ $t('campaign.creative_tab') | capitalize }}</div>
					<div class="v2rt-card__sub">Per-creative audit by SSP agency</div>
				</div>
			</div>

			<div v-if="creativeList && creativeList.length" class="v2rt-creative-list">
				<div
					v-for="format in creativeList"
					:key="`${format.type}-${format.resolution}`"
					class="v2rt-format-card"
				>
					<!-- Format header -->
					<div class="v2rt-format-head">
						<div class="v2rt-format-icon" :class="formatIconClass(format)">
							<!-- Monitor -->
							<svg v-if="format.type === 'videoOrImage'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :style="{ transform: isVerticalFormat(format) ? 'rotate(90deg)' : 'none' }"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/></svg>
							<!-- Audio -->
							<svg v-else-if="format.type === 'audio'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
							<!-- Other -->
							<svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/></svg>
						</div>
						<div class="v2rt-format-info">
							<div class="v2rt-format-title">{{ formatTitle(format) }}</div>
							<div v-if="format.count" class="v2rt-format-sub">{{ $tc('campaign.devices', format.count) }}</div>
						</div>
						<span class="v2rt-pill" :class="format.ads && format.ads.length ? 'v2rt-pill--blue' : 'v2rt-pill--gray'">
							{{ format.ads ? format.ads.length : 0 }} {{ format.ads && format.ads.length === 1 ? 'creative' : 'creatives' }}
						</span>
					</div>

					<!-- Format body -->
					<div class="v2rt-format-body">
						<template v-if="format.ads && format.ads.length">
							<div
								v-for="(ad, index) in format.ads"
								:key="`${ad.id}`"
								class="v2rt-creative-wrap"
							>
								<creative-preview
									v-if="ad.video !== null"
									:format="format"
									:creative="format.ads[index]"
									:context="{ instance }"
									class="v2rt-creative-preview"
									auditable
									@audit-update="onAuditUpdate"
									@remove="remove"
								/>
								<ul
									v-if="ad.attachments && ad.attachments.length"
									class="v2rt-attachments"
								>
									<li
										v-for="attachment in sortedAttachments(ad.attachments)"
										:key="attachment.id"
									>
										<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
										<a :href="attachment.file" target="_blank">{{ attachment.file_name }}</a>
									</li>
								</ul>
								<div
									v-if="isDSP && format.type !== 'other'"
									class="v2rt-upload-row"
								>
									<add-creative
										v-if="ad.video == null"
										:ad-id="ad.id"
										:context="{ instance }"
										:format="format"
										:disabled="isUploading(ad.id)"
										clean-on-upload
										@update:creativeData="pushCreative"
										@upload:start="setUploading(ad.id, true)"
										@upload:end="setUploading(ad.id, false)"
									/>
									<add-document
										:ad-id="ad.id"
										:campaign-id="instance.id"
										:group="`${format.type}-${format.resolution}`"
										:disabled="isUploading(ad.id)"
										@update:creativeData="pushCreative"
										@upload:start="setUploading(ad.id, true)"
										@upload:end="setUploading(ad.id, false)"
									/>
								</div>
							</div>
						</template>

						<!-- Empty state per format -->
						<template v-else>
							<div v-if="isDSP" class="v2rt-upload-empty">
								<add-creative
									:context="{ instance }"
									:format="format"
									clean-on-upload
									:disabled="isUploading(getGroup(format))"
									@update:creativeData="pushCreative"
									@upload:start="setUploading(getGroup(format), true)"
									@upload:end="setUploading(getGroup(format), false)"
								/>
								<add-document
									:campaign-id="instance.id"
									:group="getGroup(format)"
									:disabled="isUploading(getGroup(format))"
									@update:creativeData="pushCreative"
									@upload:start="setUploading(getGroup(format), true)"
									@upload:end="setUploading(getGroup(format), false)"
								/>
							</div>
							<div v-else class="v2rt-empty">
								<div class="v2rt-empty__icon">
									<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2"/></svg>
								</div>
								<p class="v2rt-empty__text">{{ $t('campaign.no_creative_specified') }}</p>
							</div>
						</template>
					</div>
				</div>
			</div>

			<div v-else class="v2rt-empty">
				<div class="v2rt-empty__icon">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2"/></svg>
				</div>
				<p class="v2rt-empty__text">{{ $t('campaign.no_creative_specified') }}</p>
			</div>
		</div>
	</section>
</template>


<script>
import OriginalTabCreative from '@/pages/request/review/tab-creative.vue';

export default {
	name: 'V2TabCreative',

	extends: OriginalTabCreative,

	methods: {
		isVerticalFormat(format) {
			if (!format.resolution) return false;
			const parts = format.resolution.split('x');
			if (parts.length !== 2) return false;
			return parseInt(parts[1]) > parseInt(parts[0]);
		},

		formatIconClass(format) {
			if (format.type === 'audio') return 'v2rt-format-icon--purple';
			if (format.type === 'other') return 'v2rt-format-icon--gray';
			return 'v2rt-format-icon--teal';
		},

		formatTitle(format) {
			if (format.type === 'videoOrImage') {
				const orientation = this.isVerticalFormat(format)
					? this.$t('vertical')
					: this.$t('horizontal');
				return `${this.$options.filters.capitalize(this.$t('campaign.monitor'))} · ${format.resolution} · ${orientation}`;
			}
			if (format.type === 'audio') {
				return this.$options.filters.capitalize(this.$t('campaign.audio'));
			}
			return this.$options.filters.capitalize(this.$t('campaign.other'));
		},
	},
};
</script>


<style lang="sass" scoped>
@import './tab-shared'

.v2rt
	padding: 0

.v2rt-creative-list
	display: flex
	flex-direction: column
	gap: 14px

.v2rt-format-card
	border: 1px solid rgba($border, 0.6)
	border-radius: 14px
	overflow: hidden
	background: $card

.v2rt-format-head
	display: flex
	align-items: center
	gap: 12px
	padding: 14px 18px
	background: rgba($bg, 0.3)
	border-bottom: 1px solid rgba($border, 0.4)

.v2rt-format-icon
	width: 32px
	height: 32px
	border-radius: 8px
	display: flex
	align-items: center
	justify-content: center
	flex-shrink: 0

	svg
		width: 14px
		height: 14px

	&--teal
		background: rgba($teal, 0.1)
		color: $teal

	&--purple
		background: rgba($purple, 0.1)
		color: $purple

	&--gray
		background: rgba($bg, 0.8)
		color: $muted

.v2rt-format-info
	flex: 1
	min-width: 0

.v2rt-format-title
	font-size: 13px
	font-weight: 700
	color: $text

.v2rt-format-sub
	font-size: 11px
	color: $muted
	margin-top: 2px

.v2rt-format-body
	padding: 16px
	display: flex
	flex-direction: column
	gap: 12px

.v2rt-creative-wrap
	display: flex
	flex-direction: column
	gap: 8px

// ─── Override creative-preview styling ───
.v2rt-creative-preview
	::v-deep
		// Add a soft border + radius around the V1 preview
		border: 1px solid rgba($border, 0.4)
		border-radius: 12px
		padding: 12px
		background: $card

		&:hover
			box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1)

// ─── Attachments ───
.v2rt-attachments
	list-style: none
	margin: 0
	padding: 0
	display: flex
	flex-direction: column
	gap: 6px

	li
		display: flex
		align-items: center
		gap: 8px
		font-size: 12px
		padding: 8px 12px
		background: rgba($bg, 0.4)
		border-radius: 8px

		svg
			width: 14px
			height: 14px
			color: $muted
			flex-shrink: 0

		a
			color: $teal
			text-decoration: none
			font-weight: 500

			&:hover
				text-decoration: underline

// ─── Upload rows ───
.v2rt-upload-row,
.v2rt-upload-empty
	display: flex
	flex-direction: column
	gap: 10px

	::v-deep > *
		max-width: 100% !important

// ─── Empty state ───
.v2rt-empty
	padding: 40px 20px
	text-align: center

.v2rt-empty__icon
	width: 56px
	height: 56px
	border-radius: 14px
	background: rgba($bg, 0.7)
	color: $muted
	display: flex
	align-items: center
	justify-content: center
	margin: 0 auto 12px

	svg
		width: 24px
		height: 24px

.v2rt-empty__text
	font-size: 13px
	color: $muted

// ─── Pill ───
.v2rt-pill
	display: inline-flex
	align-items: center
	padding: 2px 8px
	border-radius: 9999px
	font-size: 10px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.04em

	&--blue
		background: rgba($blue, 0.12)
		color: $blue
	&--gray
		background: rgba($bg, 0.8)
		color: $muted
</style>
